import Konva from 'konva';
import { Canvas } from 'konva/types/Canvas';
import { useCallback, useRef, useState } from 'react';

import { waitForPixelationWorkersFinished } from './linePixelation';

/**
 * A canvas or an image element costs a few hundred bytes on the JS heap while owning
 * width * height * 4 bytes of renderer memory that V8 does not account for. With full-resolution
 * media (e.g. 2048x32765 = 256MB per backing store) nothing ever triggers a GC, so the backing
 * stores pile up across media switches until the renderer is OOM-killed while the JS heap still
 * reads ~90MB. The helpers below drop the backing store rather than waiting for a collection that
 * never comes.
 */

/**
 * A stage owns a bufferCanvas and a bufferHitCanvas, and each of its layers owns a scene canvas and
 * a hit canvas - so a hidden full-resolution stage costs 4x the image, not 1x.
 */
const stageCanvases = (stage: Konva.Stage): Canvas[] =>
  [
    stage.bufferCanvas,
    stage.bufferHitCanvas,
    ...stage
      .getLayers()
      .toArray()
      .flatMap(node => {
        const layer = node as Konva.Layer;
        return [layer.getCanvas?.(), layer.getHitCanvas?.()];
      }),
  ].filter(Boolean) as Canvas[];

/**
 * Konva's `destroy()` detaches the stage and its layers but leaves all four canvases it sized to
 * the stage still allocated, so call this on unmount as well.
 */
export const releaseStageCanvases = (stageLike: Konva.Stage | null | undefined): void => {
  const stage = stageLike?.getStage();
  if (!stage) {
    return;
  }
  stageCanvases(stage).forEach(canvas => canvas.setSize(0, 0));
};

/**
 * The same release, held back until any rasterization that was already in flight has finished.
 *
 * A stage closes because the annotations changed, and the edit that changed them is being saved
 * through `layerToBitMapAnnotationsAsync` at that same moment - which hands back the layer it was
 * given only after the pixelation workers finish and two animation frames have passed. Zeroing the
 * layer's scene canvas inside that window hands it a 0x0 source: `drawImage` throws, the throw
 * lands in an animation frame callback rather than in the promise, so the save never settles and
 * the edit is neither written nor reported.
 *
 * Waiting on the same two things puts this behind any read that started first, because both queue
 * in order. A read that starts later sees the stage already gone and correctly reports no
 * annotations. The cost is a couple of frames of backing store that was going to be freed anyway.
 */
const releaseStageCanvasesAfterPendingReads = (stageLike: Konva.Stage | null | undefined): void => {
  const stage = stageLike?.getStage();
  if (!stage) {
    return;
  }
  // react-konva destroys the stage as soon as the ref is cleared, and a destroyed stage no longer
  // lists its layers - so the canvases have to be collected now. Only the zeroing can wait.
  const canvases = stageCanvases(stage);
  waitForPixelationWorkersFinished().then(() =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => canvases.forEach(canvas => canvas.setSize(0, 0))),
    ),
  );
};

/**
 * Of the four canvases Konva sizes to a `display:none` stage, two are dead weight:
 *  - the hit canvases, because a hidden stage receives no pointer events, and `listening(false)`
 *    on its layers makes `getIntersection` return before it would read one.
 *  - the pixel-ratio scale-up of the two scene canvases, because `layerToBitMapAnnotationsAsync`
 *    resamples back to the media dimensions and `extractColorFromHiddenLayer` scales its own
 *    lookup, so a HiDPI display quadruples the area for nothing.
 * The scene canvases themselves have to stay: the layer's is what those two read, and the stage's
 * buffer is what Konva composites partially transparent fill+stroke polygons through.
 *
 * Konva re-sizes all four whenever the stage size changes, and a stage mounts later than the
 * component that owns it, so this is safe to call after every render: both steps check first,
 * because `setSize` and `setPixelRatio` reassign canvas.width, which clears the canvas and
 * reallocates its backing store.
 */
export const trimHiddenStageCanvases = (stageLike: Konva.Stage | null | undefined): void => {
  const stage = stageLike?.getStage();
  if (!stage) {
    return;
  }
  const drop = (canvas?: Canvas) => {
    if (canvas && (canvas.width !== 0 || canvas.height !== 0)) {
      canvas.setSize(0, 0);
    }
  };
  const unscale = (canvas?: Canvas) => {
    if (canvas && canvas.getPixelRatio() !== 1) {
      canvas.setPixelRatio(1);
    }
  };

  drop(stage.bufferHitCanvas);
  unscale(stage.bufferCanvas);
  stage
    .getLayers()
    .toArray()
    .forEach(node => {
      const layer = node as Konva.Layer;
      drop(layer.getHitCanvas?.());
      unscale(layer.getCanvas?.());
    });
};

/**
 * A ref for a stage that can unmount while the component holding it stays mounted - a stage
 * rendered behind a condition. The owner's unmount cleanup never runs in that case, so the stage's
 * canvases would be left allocated and unreachable. react-konva clears the forwarded ref before it
 * destroys the stage, so the null call is the moment to release.
 */
export const useReleasingStageRef = () => {
  const ref = useRef<Konva.Stage | null>(null);
  const setRef = useCallback((stage: Konva.Stage | null) => {
    if (stage) {
      ref.current = stage;
    } else {
      releaseStageCanvasesAfterPendingReads(ref.current);
      ref.current = null;
    }
  }, []);
  return [ref, setRef] as const;
};

/**
 * A hidden media-resolution stage, mounted in two steps so its canvases are never allocated at the
 * wrong size in the first place.
 *
 * Konva reads the global `Konva.pixelRatio` when it constructs and sizes a canvas, so a stage
 * rendered straight to media dimensions has already allocated DPR-scaled backing stores by the
 * time any effect could shrink them - four times the area on a 2x display, which on large media is
 * the allocation that kills the tab. Render it at 0x0 for one commit instead: `pin` runs in the
 * owner's layout effect, once react-konva has built the stage but before `sized` lets it take its
 * real dimensions, and pins the pixel ratio to 1 so every later resize uses it.
 *
 * `sized` is per stage rather than shared, so a stage that mounts later - when a condition opens -
 * still gets pinned before it is sized.
 */
export const useHiddenStage = () => {
  const ref = useRef<Konva.Stage | null>(null);
  const [sized, setSized] = useState(false);

  const setRef = useCallback((stage: Konva.Stage | null) => {
    if (stage) {
      ref.current = stage;
    } else {
      releaseStageCanvasesAfterPendingReads(ref.current);
      ref.current = null;
      setSized(false);
    }
  }, []);

  const pin = useCallback(() => {
    if (ref.current) {
      trimHiddenStageCanvases(ref.current);
      setSized(true);
    }
  }, []);

  return { ref, setRef, sized, pin };
};

/**
 * Removing `src` frees the decoded bitmap. Assigning `''` instead would resolve against the
 * document URL and fire a spurious request.
 */
export const releaseImageBitmap = (image: HTMLImageElement | null | undefined): void => {
  image?.removeAttribute('src');
};
